import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { YouTubeChannel } from '@kidstube/types';

interface ChannelPillsProps {
  channels: YouTubeChannel[];
  activeChannelId?: string;
}

export default function ChannelPills({ channels, activeChannelId }: ChannelPillsProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanScrollLeft(scroller.scrollLeft > 4);
    setCanScrollRight(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    updateScrollButtons();
    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(scroller);
    scroller.addEventListener('scroll', updateScrollButtons, { passive: true });

    return () => {
      resizeObserver.disconnect();
      scroller.removeEventListener('scroll', updateScrollButtons);
    };
  }, [channels, updateScrollButtons]);

  useEffect(() => {
    if (!activeChannelId) return;
    const active = scrollerRef.current?.querySelector<HTMLElement>('.channel-pill.active');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeChannelId]);

  const scrollByPage = (direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollBy({
      left: direction * Math.max(240, scroller.clientWidth * 0.75),
      behavior: 'smooth',
    });
  };

  return (
    <div className="channel-pills-shell">
      <button
        type="button"
        className={`channel-scroll-button channel-scroll-button-left ${canScrollLeft ? 'is-visible' : ''}`}
        aria-label="Scroll channels left"
        onClick={() => scrollByPage(-1)}
        tabIndex={canScrollLeft ? 0 : -1}
      >
        ‹
      </button>
      <div className="channel-pills" ref={scrollerRef}>
        <Link
          to="/"
          className={`channel-pill ${!activeChannelId ? 'active' : ''}`}
        >
          ✨ All
        </Link>
        {channels.map((channel) => (
          <Link
            key={channel.id}
            to={`/channel/${channel.id}`}
            className={`channel-pill ${activeChannelId === channel.id ? 'active' : ''}`}
          >
            <img src={channel.thumbnail} alt="" />
            {channel.title}
          </Link>
        ))}
      </div>
      <button
        type="button"
        className={`channel-scroll-button channel-scroll-button-right ${canScrollRight ? 'is-visible' : ''}`}
        aria-label="Scroll channels right"
        onClick={() => scrollByPage(1)}
        tabIndex={canScrollRight ? 0 : -1}
      >
        ›
      </button>
    </div>
  );
}
